/**
 * @file Which art each character-driven entity (player, eventually enemies)
 * wears.
 *
 * Same split as {@link ./WeaponArt.js}: this is presentation, not balance. A
 * key missing here still works — the renderer falls back to the procedural
 * capsule it used before real models existed.
 *
 * Source art is Kenney's "Animated Characters 2" (CC0); one rigged body
 * (`characterMedium`) shared by every skin. The raw FBX was converted to
 * `.glb` with `scripts/convert-character.mjs` because {@link ../core/AssetLoader.js}
 * only speaks glTF — the licence travels with the copies in
 * `assets/models/characters/`.
 *
 * The `?url` imports are what let Vite fingerprint and rewrite these files for
 * the `/frenzy/` base path — never build these paths by hand.
 */

import { PALETTE } from './Palette.js';

import characterMediumModel from '../../assets/models/characters/characterMedium.glb?url';
import skaterMaleASkin from '../../assets/models/characters/skaterMaleA.png?url';

/**
 * Metres of character per unit of source model. Tuned by eye against the
 * procedural capsule it replaces, not a literal FBX unit conversion.
 */
export const CHARACTER_MODEL_SCALE = 0.006;

/**
 * @typedef {object} CharacterArt
 * @property {string} model URL of the `.glb` body (skinned mesh, no material).
 * @property {string} skin URL of the diffuse texture painted onto it.
 * @property {number} tint Flat emissive colour (not modulated by the skin
 *   texture — a dark pixel must glow exactly as much as a bright one, the same
 *   reasoning as the weapons' uniform re-tint). Without this a textured body
 *   reads as near-black in the arena's low ambient light, the way the capsule
 *   this replaces never could.
 * @property {number} [scale] Overrides {@link CHARACTER_MODEL_SCALE}.
 * @property {number} [rotationY] Extra yaw so the model's forward matches the
 *   body's aim direction (+Z).
 */

/**
 * Entity key → art. `player` is the only row today; enemy archetypes will
 * gain rows here once they get real models too.
 * @type {Readonly<Record<string, CharacterArt>>}
 */
export const CHARACTER_ART = Object.freeze({
  player: { model: characterMediumModel, skin: skaterMaleASkin, tint: PALETTE.player }
});

/**
 * @returns {Array<{ key: string, url: string, type: 'model' | 'texture' }>}
 *   Manifest for {@link ../core/AssetLoader.js AssetLoader}. Each entity gets
 *   its own model key even when the source `.glb` is shared, so a future
 *   second skin on the same body still yields an independently prepared copy.
 */
export function characterModelManifest() {
  return Object.entries(CHARACTER_ART).flatMap(([key, art]) => [
    { key: characterModelKey(key), url: art.model, type: /** @type {'model'} */ ('model') },
    { key: characterSkinKey(key), url: art.skin, type: /** @type {'texture'} */ ('texture') }
  ]);
}

/**
 * @param {string} key Character entity key.
 * @returns {string} Asset-loader key for that entity's body model.
 */
export function characterModelKey(key) {
  return `character-model:${key}`;
}

/**
 * @param {string} key Character entity key.
 * @returns {string} Asset-loader key for that entity's skin texture.
 */
export function characterSkinKey(key) {
  return `character-skin:${key}`;
}
